import { ref, shallowRef, computed } from 'vue';
import { MabiDB } from '@/mabidb';
import { ActorManager } from '@/eventActor';
import { DamageCollectorManager } from '@/actionCollector';

export * from './loadingState';

const defaultRegion = 'us';

export const region = ref(defaultRegion);
export const lang = ref(defaultRegion);
export const regionList = ref([defaultRegion]);

// Singleton MabiDB instance to preserve memory cache
const dbInstance = new MabiDB(region.value, lang.value);
export const db = shallowRef(dbInstance);

export const raceNameMap = ref<Record<number, string>>({});
export const skillNameMap = ref<Record<number, string>>({});
export const condNameMap = ref<Record<number, string>>({});
export const trackedStatuses = ref<number[]>([]);
export const trackedBosses = ref<number[]>([]);
export const anonymizeMode = ref(false);
export const optimizationMode = ref(true); // Default to True for performance

// Configurable filters for target entity dropdowns
export interface AnalysisFilters {
    minEntityDamage: number; // Minimum total damage taken to show in dropdown
    minDebuffCount: number;  // Minimum number of debuff statuses to show in dropdown
}
export const analysisFilters = ref<AnalysisFilters>({
    minEntityDamage: 10_000_000,
    minDebuffCount: 0,
});

export interface SkillClass {
    name: string;
    skills: number[];
}
// Resource Loading State
export const resourcesReady = ref(false);
export const resourceLoadingProgress = ref(0); // 0-100 or just loaded bytes

export const skillClasses = ref<any[]>([]);

// Config > Skill Amplification: these skills are amplified by these statuses
// (onTarget: checked on the hit target instead of the attacker).
export type SkillAmpStatus = { id: number; onTarget: boolean };
export type SkillAmpEntry = { name: string; skills: number[]; statuses: SkillAmpStatus[] };
export const skillAmpConfig = ref<SkillAmpEntry[]>([]);

export interface StatusGroup {
    name: string;
    statuses: number[];
}
export const statusGroups = ref<StatusGroup[]>([]);

// Weapon name mapping
export const weaponNameMap = ref<Record<number, string>>({});

export function getPrimaryClass(entity: any): string | undefined {
    if (!skillClasses.value.length) return undefined;

    const classCounts = new Map<string, number>();

    // Optimization Mode: Use aggregatedStats (Refinement 123)
    const agg = entity.aggregatedStats || entity.AggregatedStats;
    if (agg) {
        for (const targetId in agg) {
            const targetStats = agg[targetId];
            if (!targetStats) continue;
            const skills = targetStats.skills || targetStats.Skills;
            if (!skills) continue;

            for (const skillId in skills) {
                const skillStats = skills[skillId];
                if (!skillStats) continue;
                const count = skillStats.count ?? skillStats.Count ?? 0;
                
                // Count by skill usage count (more accurate than individual hits)
                for (const cls of skillClasses.value) {
                    if (cls.skills.includes(+skillId)) {
                        classCounts.set(cls.name, (classCounts.get(cls.name) || 0) + count);
                    }
                }
            }
        }
    }
    // Legacy Mode: Use applyDamages
    else if (entity.applyDamages?.length) {
        for (const damage of entity.applyDamages) {
            for (const cls of skillClasses.value) {
                if (cls.skills.includes(damage.SkillId)) {
                    classCounts.set(cls.name, (classCounts.get(cls.name) || 0) + 1);
                }
            }
        }
    } else {
        return undefined;
    }

    // Find class with maximum skill usage count
    let maxClass = undefined;
    let maxCount = 0;

    for (const [clsName, count] of classCounts) {
        if (count > maxCount) {
            maxCount = count;
            maxClass = clsName;
        }
    }
    return maxClass;
}

export function getPrimaryWeapon(entity: any): string | undefined {
    if (!entity) return undefined;
    const weaponUsage = entity.weaponUsageByClass || entity.WeaponUsage;
    
    if (weaponUsage && skillClasses.value.length) {
        const primaryClass = getPrimaryClass(entity);
        if (primaryClass) {
            const classWeapons = weaponUsage[primaryClass];
            if (classWeapons) {
                let maxCount = 0;
                let result: string | undefined = undefined;

                for (const weaponKey in classWeapons) {
                    if (classWeapons[weaponKey] > maxCount) {
                        maxCount = classWeapons[weaponKey];

                        const parts = weaponKey.split('_');
                        const names: string[] = [];
                        for (const partId of parts) {
                            const name = weaponNameMap.value[+partId];
                            if (name) names.push(name);
                        }
                        if (names.length > 0) {
                            result = names.join('\n');
                        }
                    }
                }
                if (result) return result;
            }
        }
    }

    // Fallback: Use directly equipped items from entity related persistent events
    const equipMap = entity.equipItemMap || entity.EquipItemMap;
    if (equipMap) {
        const primaryWeapon = equipMap[10];
        const secondaryWeapon = equipMap[12];
        const ammo = equipMap[14];

        if (primaryWeapon && primaryWeapon > 0) {
            let weaponKey = `${primaryWeapon}`;
            if (secondaryWeapon && secondaryWeapon > 0) weaponKey += `_${secondaryWeapon}`;
            if (ammo && ammo > 0) weaponKey += `_${ammo}`;
            
            const parts = weaponKey.split('_');
            const names: string[] = [];
            for (const partId of parts) {
                const name = weaponNameMap.value[+partId];
                if (name) names.push(name);
            }
            if (names.length > 0) {
                return names.join('\n');
            }
        }
    }

    return undefined;
}

export const itemNameMap = ref<Record<number, string>>({});
export const appEvent = ref(new EventTarget());

// Track currently loaded session for auto-clear and UI indicator
export const currentSessionName = ref<string | undefined>(undefined);

export const liveSyncState = shallowRef<any>(null);

export const dcManager = shallowRef(new DamageCollectorManager());
export const actorManager = shallowRef(new ActorManager(dcManager.value as DamageCollectorManager));

// Refinement 120: Reset Store Logic
export function resetStore() {
    // Force garbage collection of old managers by replacing them entirely
    const newDc = new DamageCollectorManager();
    dcManager.value = newDc;
    actorManager.value = new ActorManager(newDc);

    // Reset other session specific state
    currentSessionName.value = undefined;
    liveSyncState.value = null;
    appEvent.value.dispatchEvent(new CustomEvent('clear'));

    console.log('[Store] Full reset complete');
}

export interface User {
    id: string;
    username: string;
    avatar: string;
    role: number;
    // Server-wide -leaderboard flag stamped onto every /api/me response
    // (see handleMe) - not part of the user's own identity.
    leaderboardEnabled?: boolean;
}

export const currentUser = ref<User | null>(null);
export const isAuthenticated = computed(() => !!currentUser.value);
